// src/components/shell/MobileBottomTabBar.tsx
// Mobile-only fixed-bottom tab bar that shows the modes belonging to the
// active bucket. Mounted by the workspace shell when the viewport is mobile.
//
// Per dev-sync note a57c0565 — the manufacturing bucket has 6+ accessible
// modes (Inventory, Models, Orders, Plants, Build pipeline, and optionally
// Fulfill / Reports / Admin Home). Without overflow handling the bar
// either crushes labels into illegible chips or wraps to two rows, which
// reads as a "stacked double bottom bar" and looks materially different
// from how Sales / Manager / Admin buckets render. We now keep the bar at
// a single row by showing the active mode + N siblings (up to OVERFLOW_AT
// minus one), and grouping the rest under a "More" overflow sheet using
// the same visual treatment as the inline tabs.
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useAnchorEntitlements } from "@/contexts/AnchorEntitlementsContext";
import {
  MODE_GLYPH,
  getAccessibleModesInBucket,
  type PrimaryMode,
} from "@/components/shell/modes/modesRegistry";
import { resolveActiveBucket } from "@/components/shell/modes/bucketsRegistry";
import { ModeUrgencyBadge } from "@/components/shell/ModeUrgencyBadge";
import {
  FIREFLY_TARGET_ATTR,
  fireflyModeNavTargetId,
} from "@/lib/firefly/fireflyTargetConvention";
import { MobileMessengerTabButton } from "@/components/shell/MobileMessengerTabButton";

export type MobileBottomTabBarProps = {
  mode: PrimaryMode;
  setMode: (m: PrimaryMode) => void;
};

// Single-row budget on the narrowest supported viewport (360px Android).
// We keep at most this many inline cells; anything beyond becomes "More".
const OVERFLOW_AT = 5;

export function MobileBottomTabBar({ mode, setMode }: MobileBottomTabBarProps) {
  const auth = useAuth();
  const { anchors } = useAnchorEntitlements();
  const activeBucketId = resolveActiveBucket(mode, auth, anchors);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreSheetRef = useRef<HTMLDivElement | null>(null);

  // Hooks must run unconditionally; bail returns happen after both hooks.
  useEffect(() => {
    if (!moreOpen) return;
    const handlePointer = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!moreSheetRef.current || !target) return;
      if (!moreSheetRef.current.contains(target)) setMoreOpen(false);
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMoreOpen(false);
    };
    window.addEventListener("pointerdown", handlePointer);
    window.addEventListener("keydown", handleKey);
    return () => {
      window.removeEventListener("pointerdown", handlePointer);
      window.removeEventListener("keydown", handleKey);
    };
  }, [moreOpen]);

  if (!activeBucketId) return null;
  const modes = getAccessibleModesInBucket(activeBucketId, auth, anchors);
  if (modes.length === 0) return null;

  const needsOverflow = modes.length > OVERFLOW_AT;
  let inlineModes = modes;
  let overflowModes: typeof modes = [];

  if (needsOverflow) {
    // Strategy: keep the active mode visible inline at all times so the
    // user always sees a highlighted tab, then fill remaining inline slots
    // with the earliest siblings. Anything pushed out goes into the More
    // sheet. inlineModes slot count = OVERFLOW_AT - 1 (last cell is More).
    const visibleSlots = OVERFLOW_AT - 1;
    const activeIndex = modes.findIndex((m) => m.id === mode);
    const head = modes.slice(0, visibleSlots);
    if (activeIndex >= 0 && activeIndex >= visibleSlots) {
      // Active mode would be overflowed — swap it into the last visible slot
      // and demote whatever was there into the overflow sheet.
      const adjusted = [...head];
      adjusted[adjusted.length - 1] = modes[activeIndex];
      inlineModes = adjusted;
      overflowModes = modes.filter((m) => !adjusted.some((a) => a.id === m.id));
    } else {
      inlineModes = head;
      overflowModes = modes.slice(visibleSlots);
    }
  }

  return (
    // iOS Safari note: `inset-x-0 bottom-0` keeps the bar pinned to the visual
    // viewport bottom; `paddingBottom: env(safe-area-inset-bottom)` keeps the
    // tap targets above the bottom URL bar. The `viewport-fit=cover` meta tag
    // (set in index.html) is what makes that env() value resolve to a
    // positive number on iOS. The whole-page `min-height: 100dvh` rule in
    // index.css is what keeps the bar visible on the initial paint instead of
    // showing only after the first scroll. Dev-sync note 910dfdae.
    <nav
      aria-label="Workspace modes"
      className="fixed inset-x-0 bottom-0 z-30 border-t border-gray-200 bg-white/95 backdrop-blur dark:border-zinc-800 dark:bg-zinc-950/95"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <ul className="flex w-full items-stretch">
        {inlineModes.map((entry) => (
          <li key={entry.id} className="flex-1 min-w-0">
            <ModeButton entry={entry} active={entry.id === mode} currentMode={mode} onSelect={setMode} />
          </li>
        ))}
        {needsOverflow ? (
          <li className="flex-1 min-w-0">
            <button
              type="button"
              onClick={() => setMoreOpen((v) => !v)}
              aria-expanded={moreOpen}
              aria-haspopup="menu"
              title={`More (${overflowModes.length})`}
              className={[
                "flex w-full flex-col items-center justify-center gap-1 px-1 py-1.5 text-[11px] transition-colors",
                overflowModes.some((m) => m.id === mode)
                  ? "text-indigo-700 dark:text-indigo-200"
                  : "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100",
              ].join(" ")}
            >
              <span
                className={[
                  "relative flex h-7 w-7 items-center justify-center rounded-lg text-[10px] font-semibold",
                  moreOpen
                    ? "bg-indigo-600 text-white"
                    : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300",
                ].join(" ")}
              >
                <span aria-hidden>···</span>
              </span>
              <span className="block w-full truncate text-center leading-tight">More</span>
            </button>
          </li>
        ) : null}
        <MobileMessengerTabButton />
      </ul>
      {moreOpen
        ? createPortal(
            <div
              ref={moreSheetRef}
              role="menu"
              aria-label="More workspace modes"
              // Anchored just above the tab bar; uses safe-area padding too so
              // it tracks the iOS home indicator like the bar itself.
              className="fixed inset-x-2 bottom-[calc(env(safe-area-inset-bottom)+56px)] z-40 rounded-2xl border border-gray-200 bg-white p-2 shadow-2xl dark:border-zinc-800 dark:bg-zinc-950"
            >
              <ul className="grid grid-cols-3 gap-1">
                {overflowModes.map((entry) => (
                  <li key={entry.id}>
                    <ModeButton
                      entry={entry}
                      active={entry.id === mode}
                      currentMode={mode}
                      onSelect={(id) => {
                        setMode(id);
                        setMoreOpen(false);
                      }}
                    />
                  </li>
                ))}
              </ul>
            </div>,
            document.body
          )
        : null}
    </nav>
  );
}

function ModeButton({
  entry,
  active,
  currentMode,
  onSelect,
}: {
  entry: { id: PrimaryMode; label: string };
  active: boolean;
  currentMode: PrimaryMode;
  onSelect: (id: PrimaryMode) => void;
}) {
  return (
    <button
      type="button"
      {...{ [FIREFLY_TARGET_ATTR]: fireflyModeNavTargetId(entry.id) }}
      onClick={() => onSelect(entry.id)}
      aria-current={active ? "page" : undefined}
      title={entry.label}
      className={[
        "flex w-full flex-col items-center justify-center gap-1 px-1 py-1.5 text-[11px] transition-colors",
        active
          ? "text-indigo-700 dark:text-indigo-200"
          : "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100",
      ].join(" ")}
    >
      <span
        className={[
          "relative flex h-7 w-7 items-center justify-center rounded-lg text-[10px] font-semibold",
          active
            ? "bg-indigo-600 text-white"
            : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300",
        ].join(" ")}
      >
        <span aria-hidden>{MODE_GLYPH[entry.id]}</span>
        <ModeUrgencyBadge mode={entry.id} currentMode={currentMode} size="mobile" />
      </span>
      <span className="block w-full truncate text-center leading-tight">{entry.label}</span>
    </button>
  );
}
